import React, { useState, useMemo } from 'react'
import { Button, Popover, Tabs, Typography } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { setPeriodGroupFilter, setPeriodGroups, setPeriodGroupSelection } from '../../../redux/context/filters/actions'
import { filtersSelector, periodGroupSelectionLabelSelector } from '../../../redux/context/filters/selectors'
import { Store } from '../../../redux/types'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { PeriodGroupSelection } from '../../../redux/context/filters/types'
import { AppDispatch } from '../../../redux/store'
import LucideIcon from '../../../shared/components/LucideIcon'
import FiscalYearTab from './FiscalYearTab'
import CustomPeriodTab from './CustomPeriodTab'
import QuartersTab from './QuartersTab'

const ContextPeriodSelect: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const filters = useSelector(filtersSelector)
  const selectionLabel = useSelector(periodGroupSelectionLabelSelector)
  const selection = useSelector((store: Store) => store.context?.filters?.periodGroupSelection)

  const [open, setOpen] = useState(false)
  const hasPeriodGroups = !!filters.periodGroups?.length
  const [activeTab, setActiveTab] = useState<string>('fiscal')

  const handleSubmit = (periodGroups: PeriodGroup[], periodGroupSelection: PeriodGroupSelection) => {
    dispatch(setPeriodGroupFilter(undefined))
    dispatch(setPeriodGroups(periodGroups))
    dispatch(setPeriodGroupSelection(periodGroupSelection))
    setOpen(false)
  }

  const handleClear = () => {
    dispatch(setPeriodGroups())
    dispatch(setPeriodGroupFilter(undefined))
    dispatch(setPeriodGroupSelection(undefined))
    setOpen(false)
  }

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible)
    if (visible && selection) {
      setActiveTab(selection.type === 'fiscalYear' ? 'fiscal' : selection.type)
    }
  }

  const tabItems = useMemo(() => {
    const fiscalInitial = selection?.type === 'fiscalYear' ? selection : undefined
    const customInitial = selection?.type === 'custom' ? selection : undefined
    const quartersInitial = selection?.type === 'quarters' ? selection : undefined

    return [
      {
        key: 'fiscal',
        label: t('periodSelect:fiscal-year'),
        children: <FiscalYearTab initialSelection={fiscalInitial} onSubmit={handleSubmit} onClear={handleClear} />
      },
      {
        key: 'custom',
        label: t('periodSelect:custom-period'),
        children: <CustomPeriodTab initialSelection={customInitial} onSubmit={handleSubmit} onClear={handleClear} />
      },
      {
        key: 'quarters',
        label: t('periodSelect:quarters'),
        children: <QuartersTab initialSelection={quartersInitial} onSubmit={handleSubmit} onClear={handleClear} />
      }
    ]
  }, [selection, open])

  const content = (
    <div style={{ minWidth: 360 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        {t('periodSelect:title')}
      </Typography.Text>
      <Tabs centered size="small" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </div>
  )

  return (
    <Popover
      trigger="click"
      arrow={false}
      placement="bottomLeft"
      open={open}
      onOpenChange={handleOpenChange}
      content={content}
    >
      <Button
        data-cy="context-period-select"
        className={hasPeriodGroups ? 'active' : undefined}
        icon={<LucideIcon icon={Calendar} />}
      >
        <Typography.Text ellipsis style={{ maxWidth: 250 }}>
          {selectionLabel || t('periodSelect:period-select')}
        </Typography.Text>
        <DownOutlined style={{ fontSize: 10, color: 'rgba(0, 0, 0, 0.25)' }} />
      </Button>
    </Popover>
  )
}

export default ContextPeriodSelect
